import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const CustomDateTimePicker = ({
  label,
  id,
  value,
  onDateChange,
  isMandatoryField = false,
  disablePastDate = false,
  disableFutureDate = true,
  minimumDate,
  disabled,
  error,
  helperText,
  maxDate,
  isRefilByOtherField = false,
  isEdit = false,
  isView = false,
  displayDefaultValue = true,
  modal,
  freezeDate = false,
  allowManualInput = true
}) => {
  const [selectValue, setSelectValue] = useState(displayDefaultValue ? dayjs() : null);
  const [isManualInputError, setIsManualInputError] = useState(false);
  // const [open, setOpen] = useState(false);

  const minimumDates = minimumDate ? dayjs(minimumDate, "YYYY-MM-DDTHH:mm:ss") : null;
  const fourYearsAgo = dayjs().subtract(50, "year");
  const currentDate = dayjs(); // Current date and time
  const maxDateTime = maxDate ? dayjs(maxDate, "YYYY-MM-DDTHH:mm:ss") : null;

  const mandatoryRequiredButton = useSelector(
    (state) => state.mandatoryFields.isMandatoryRequiredEnabled
  );
  const viewEnabled = useSelector(
    (state) => state.mandatoryFields.isViewEnabled
  );

  useEffect(() => {
    if (value) {
      const parsedDate = dayjs(value, "YYYY-MM-DDTHH:mm:ss");
      if (parsedDate.isValid()) {
        setSelectValue(parsedDate);
      } else {
        setSelectValue(dayjs());
      }
    } else {
      setSelectValue(displayDefaultValue ? dayjs() : null);
    }
  }, [value, displayDefaultValue]);

  const handleDateChange = (date) => {
    if (date && date.isValid()) {
      const currentFrozenDate = freezeDate && selectValue ? selectValue.format('YYYY-MM-DD') : null;
      const adjustedDate = freezeDate ? dayjs(`${currentFrozenDate}T${date.format('HH:mm:ss')}`) : date;

      setSelectValue(adjustedDate);
      setIsManualInputError(false);
      onDateChange(adjustedDate.format("YYYY-MM-DDTHH:mm:ss"));
    } else {
      setSelectValue(null);
      setIsManualInputError(true);
      onDateChange(null);
    }
  };

  const handleInputChange = (event) => {
    const inputDate = event.target.value;
    const parsedDate = dayjs(inputDate, "DD/MM/YYYY HH:mm", true);

    if (parsedDate.isValid()) {
      const currentFrozenDate = freezeDate && selectValue ? selectValue.format('YYYY-MM-DD') : null;
      const adjustedDate = freezeDate ? dayjs(`${currentFrozenDate}T${parsedDate.format('HH:mm:ss')}`) : parsedDate;

      setSelectValue(adjustedDate);
      setIsManualInputError(false);
      onDateChange(adjustedDate.format("YYYY-MM-DDTHH:mm:ss"));
    } else if (!inputDate) {
      setSelectValue(null);
      setIsManualInputError(false);
      onDateChange(null);
    } else {
      setIsManualInputError(true);
    }
  };


  const handleLabelClick = () => {
    document.getElementById(id)?.focus();
  };

  
  return (
    <FormControl fullWidth>
      <Typography variant="labelSm" color={"text.primary"} component={"label"} onClick={handleLabelClick}>
        {label}
        {isMandatoryField && <Typography component={"span"} color={"error"}>*</Typography>}
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          id={id}
          value={selectValue}
          onChange={handleDateChange}
          ampm={false}
          format="DD/MM/YYYY HH:mm"
          disableFuture={disableFutureDate}
          disablePast={disablePastDate}
          shouldDisableDate={(date) =>
            freezeDate && selectValue ? date.format('YYYY-MM-DD') !== selectValue.format('YYYY-MM-DD') : false
          } 
          minDateTime={minimumDates && minimumDates.isValid() ? minimumDates : fourYearsAgo}
          // maxDateTime={dayjs()}
          maxDateTime={disableFutureDate ? currentDate : maxDateTime}
          disabled={
            viewEnabled ||
            (mandatoryRequiredButton && !isMandatoryField) ||
            disabled ||
            isRefilByOtherField ||
            isView
          }
          slotProps={{
            textField: {
              error: error,
                readOnly: !allowManualInput, 

              InputProps: {
                placeholder: disabled ? "" : "",
              },
            },
            popper: {
              className: modal && "zIndex-1400",
            },
            actionBar: {
              sx: { display: 'none' },
            },
          }}

          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              onChange={handleInputChange}
              helperText={isManualInputError ? "Please enter a valid date and time" : helperText}
              error={error || isManualInputError}
              InputProps={{
                ...params.InputProps,
                readOnly: false,
              }}
            />
          )}
        />
      </LocalizationProvider>
      {(helperText || isManualInputError) && <FormHelperText error={error || isManualInputError}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default CustomDateTimePicker;